/**
 * Universal Course Data Resolver
 * Consolidates all course data from all categories for complete course access
 */

import { getAllCourses } from '@/data/courses';
import { SQL_COURSES, SQL_ADVANCED_PHASE_COURSES } from '@/data/sql';
import { SQL_PHASE1_COURSES } from '@/data/sql/phase1';
import { PYTHON_COURSES } from '@/data/pythonCourseData';
import { AI_COURSES } from '@/data/ai/courses';
import { JAVASCRIPT_COURSES } from '@/data/javascript';
import { AZURE_COURSES } from '@/data/azure';
import { TABLEAU_COURSES } from '@/data/tableau';
import { POWERBI_COURSES } from '@/data/powerbi';
import { AWS_COURSES } from '@/data/aws/courses';
import { GCP_COURSES } from '@/data/gcp';
import { JAVA_COURSES } from '@/data/java';
import { DOTNET_COURSES } from '@/data/dotnet';
import { CSHARP_COURSES } from '@/data/csharp/courses';
import { DATASCIENCE_COURSES } from '@/data/datascience';
import { DATAENGINEERING_COURSES } from '@/data/dataengineering';
import { ETL_COURSES } from '@/data/etl';
import { MOBILE_COURSES } from '@/data/mobile';
import { BACKEND_COURSES } from '@/data/backend';
import { DATABASE_COURSES } from '@/data/database';
import { SECURITY_COURSES } from '@/data/security';
import { BLOCKCHAIN_COURSES } from '@/data/blockchain';
import { ENTERPRISE_TRAINING_COURSES } from '@/data/enterprise-training';
import { UI_UX_COURSES } from '@/data/ui-ux-design';
import { GRAPHICS_PROGRAMMING_COURSES } from '@/data/graphics-programming';
import { DEVOPS_COURSES } from '@/data/devops';
import { APPSEC_COURSES } from '@/data/appsec';
import { PROFESSIONAL_DEVELOPMENT_COURSES } from '@/data/professional-development';
import { INFRASTRUCTURE_COURSES } from '@/data/infrastructure';
import { FRONTEND_ADVANCED_COURSES } from '@/data/frontend-advanced';
import { TESTING_MODERN_COURSES } from '@/data/testing-modern';
import { WEB3_DEVELOPMENT_COURSES } from '@/data/web3-development';
import { SERVERLESS_EDGE_COURSES } from '@/data/serverless-edge';
import { SELENIUM_COURSES } from '@/data/selenium/courses';
import { RPA_COURSES } from '@/data/rpa';
import { GRAPHICS_COURSES } from '@/data/graphics';
import { DATA_SCIENCE_COURSES } from '@/data/data-science';
import { UNITY_GAME_DEVELOPMENT, UNREAL_GAME_DEVELOPMENT, AR_VR_DEVELOPMENT, DESKTOP_APPLICATION_DEVELOPMENT } from '@/data/game-development';
import { adaptETLCourseToCourse } from './etlTypeAdapters';
// New course categories
import { RUST_COURSES } from '@/data/rust';
import { MODERN_FRONTEND_COURSES } from '@/data/modern-frontend';
import { KUBERNETES_MASTERY_COURSES } from '@/data/kubernetes-mastery';
import { EDGE_COMPUTING_COURSES } from '@/data/edge-computing';
import { CROSS_PLATFORM_COURSES } from '@/data/cross-platform';
import { GREEN_TECH_COURSES } from '@/data/green-tech';
import { NO_CODE_COURSES } from '@/data/no-code';
import { ADVANCED_API_COURSES } from '@/data/advanced-api';

export interface UniversalCourse {
  id: string;
  title: string;
  description: string;
  level: string;
  totalDuration: string;
  image: string;
  prerequisites: string[];
  learningOutcomes: string[];
  modules: any[];
  type?: string; // Optional to match Course interface
  courseType?: string; // Optional to match Course interface
  metadata?: {
    resources?: Array<{
      title: string;
      url: string;
      type: 'documentation' | 'tool' | 'reference';
      description: string;
      isPrimary?: boolean;
    }>;
  };
}

/**
 * Normalizes course data to a consistent format
 */
const normalizeCourse = (course: any, courseType: string): UniversalCourse => {
  // Special handling for ETL courses - adapt them to standard format first
  if (courseType === 'etl' && course.toolFocus) {
    // This is an ETL course, adapt it
    const adaptedCourse = adaptETLCourseToCourse(course);
    return {
      id: adaptedCourse.id,
      title: adaptedCourse.title,
      description: adaptedCourse.description,
      level: adaptedCourse.level,
      totalDuration: adaptedCourse.totalDuration,
      image: adaptedCourse.image,
      prerequisites: adaptedCourse.prerequisites,
      learningOutcomes: adaptedCourse.learningOutcomes,
      modules: adaptedCourse.modules,
      type: courseType,
      courseType: courseType
    };
  }

  // Standard normalization for other courses
  return {
    id: course.id,
    title: course.title,
    description: course.description,
    level: course.level,
    totalDuration: course.totalDuration || course.duration || 'Not specified',
    image: course.image || course.imageSrc || '/api/placeholder/400/300',
    prerequisites: course.prerequisites || course.requirements || [],
    learningOutcomes: course.learningOutcomes || course.learningObjectives || [],
    modules: course.modules || [],
    type: courseType,
    courseType: courseType
  };
};

/**
 * Gets all courses from all categories
 */
export const getAllUniversalCourses = (): UniversalCourse[] => {
  const courses: UniversalCourse[] = [];

  // Add courses from all categories - complete list of 120+ courses across 39+ categories
  const courseCategories = [
    { courses: SQL_COURSES, type: 'sql' },
    { courses: SQL_PHASE1_COURSES, type: 'sql' },
    { courses: SQL_ADVANCED_PHASE_COURSES, type: 'sql' },
    { courses: PYTHON_COURSES, type: 'python' },
    { courses: AI_COURSES, type: 'ai' },
    { courses: JAVASCRIPT_COURSES, type: 'javascript' },
    { courses: AZURE_COURSES, type: 'azure' },
    { courses: TABLEAU_COURSES, type: 'tableau' },
    { courses: POWERBI_COURSES, type: 'powerbi' },
    { courses: AWS_COURSES, type: 'aws' },
    { courses: GCP_COURSES, type: 'gcp' },
    { courses: JAVA_COURSES, type: 'java' },
    { courses: DOTNET_COURSES, type: 'dotnet' },
    { courses: CSHARP_COURSES, type: 'csharp' },
    { courses: DATASCIENCE_COURSES, type: 'datascience' },
    { courses: DATAENGINEERING_COURSES, type: 'dataengineering' },
    { courses: ETL_COURSES, type: 'etl' },
    { courses: MOBILE_COURSES, type: 'mobile' },
    { courses: BACKEND_COURSES, type: 'backend' },
    { courses: DATABASE_COURSES, type: 'database' },
    { courses: SECURITY_COURSES, type: 'security' },
    { courses: BLOCKCHAIN_COURSES, type: 'blockchain' },
    { courses: ENTERPRISE_TRAINING_COURSES, type: 'enterprise-training' },
    { courses: UI_UX_COURSES, type: 'ui-ux-design' },
    { courses: GRAPHICS_PROGRAMMING_COURSES, type: 'graphics-programming' },
    { courses: DEVOPS_COURSES, type: 'devops' },
    { courses: APPSEC_COURSES, type: 'appsec' },
    { courses: PROFESSIONAL_DEVELOPMENT_COURSES, type: 'professional-development' },
    { courses: INFRASTRUCTURE_COURSES, type: 'infrastructure' },
    { courses: FRONTEND_ADVANCED_COURSES, type: 'frontend-advanced' },
    { courses: TESTING_MODERN_COURSES, type: 'testing-modern' },
    { courses: WEB3_DEVELOPMENT_COURSES, type: 'web3-development' },
    { courses: SERVERLESS_EDGE_COURSES, type: 'serverless-edge' },
    { courses: SELENIUM_COURSES, type: 'testing' },
    { courses: RPA_COURSES, type: 'rpa-tools' },
    { courses: GRAPHICS_COURSES, type: 'graphics' },
    { courses: DATA_SCIENCE_COURSES, type: 'data-science' },
    { courses: [UNITY_GAME_DEVELOPMENT], type: 'game-development' },
    { courses: [UNREAL_GAME_DEVELOPMENT], type: 'game-development' },
    { courses: [AR_VR_DEVELOPMENT], type: 'ar-vr' },
    { courses: [DESKTOP_APPLICATION_DEVELOPMENT], type: 'desktop-apps' },
    // New course categories
    { courses: RUST_COURSES, type: 'rust' },
    { courses: MODERN_FRONTEND_COURSES, type: 'modern-frontend' },
    { courses: KUBERNETES_MASTERY_COURSES, type: 'kubernetes-mastery' },
    { courses: EDGE_COMPUTING_COURSES, type: 'edge-computing' },
    { courses: CROSS_PLATFORM_COURSES, type: 'cross-platform' },
    { courses: GREEN_TECH_COURSES, type: 'green-tech' },
    { courses: NO_CODE_COURSES, type: 'no-code' },
    { courses: ADVANCED_API_COURSES, type: 'advanced-api' }
  ];

  // Also include the legacy courses for backward compatibility
  const legacyCourses = getAllCourses();
  legacyCourses.forEach(course => {
    courses.push(normalizeCourse(course, course.type || 'basic'));
  });

  // Add all categorized courses
  courseCategories.forEach(category => {
    if (category.courses && Array.isArray(category.courses)) {
      category.courses.forEach(course => {
        courses.push(normalizeCourse(course, category.type));
      });
    }
  });

  console.log(`✅ Universal Course Resolver loaded ${courses.length} courses`);
  return courses;
};

/**
 * Finds a course by ID across all categories
 */
export const findCourseById = (courseId: string): UniversalCourse | null => {
  const allCourses = getAllUniversalCourses();
  // Prefer the most complete course in case of duplicates (e.g., legacy vs categorized)
  const matches = allCourses.filter(c => c.id === courseId);

  if (matches.length === 0) {
    console.warn(`❌ Course not found: ${courseId}`);
    console.log('Available course IDs:', allCourses.map(c => c.id));
    return null;
  }

  // Choose the course with the most modules to avoid empty-module variants
  const course = matches.reduce((best, current) => {
    const bestLen = (best.modules?.length || 0);
    const currLen = (current.modules?.length || 0);
    return currLen > bestLen ? current : best;
  }, matches[0]);

  return course || null;
};

/**
 * Gets courses by type/category
 */
export const getCoursesByType = (courseType: string): UniversalCourse[] => {
  const allCourses = getAllUniversalCourses();
  return allCourses.filter(course => course.courseType === courseType);
};

/**
 * Gets all available course types
 */
export const getAllCourseTypes = (): string[] => {
  const allCourses = getAllUniversalCourses();
  const types = new Set(allCourses.map(course => course.courseType));
  return Array.from(types);
};

/**
 * Gets course type title for display
 */
export const getCourseTypeTitle = (courseType: string): string => {
  const titles: Record<string, string> = {
    'sql': 'SQL Database Courses',
    'python': 'Python Programming Courses',
    'ai': 'Artificial Intelligence Courses',
    'javascript': 'JavaScript Development Courses',
    'azure': 'Microsoft Azure Courses',
    'ml': 'Machine Learning Courses',
    'tableau': 'Tableau Analytics Courses',
    'powerbi': 'Power BI Courses',
    'aws': 'Amazon Web Services Courses',
    'gcp': 'Google Cloud Platform Courses',
    'java': 'Java Programming Courses',
    'dotnet': '.NET Development Courses',
    'datascience': 'Data Science Courses',
    'dataengineering': 'Data Engineering Courses',
    'mobile': 'Mobile Development Courses',
    'backend': 'Backend Development Courses',
    'database': 'Database Technology Courses',
    'security': 'Cybersecurity Courses',
    'blockchain': 'Blockchain Technology Courses',
    'enterprise-training': 'Enterprise Training Courses',
    'ui-ux-design': 'UI/UX Design Courses',
    'graphics-programming': 'Graphics Programming Courses',
    'devops': 'DevOps & Infrastructure Courses',
    'appsec': 'Application Security Courses',
    'professional-development': 'Professional Development Courses',
    'infrastructure': 'Infrastructure Courses',
    'frontend-advanced': 'Advanced Frontend Courses',
    'testing-modern': 'Modern Testing Courses',
    'web3-development': 'Web3 Development Courses',
    'serverless-edge': 'Serverless & Edge Computing Courses',
    'selenium': 'Selenium Testing Courses',
    'csharp': 'C# Programming Courses',
    'rpa-tools': 'RPA Tools & Automation Courses',
    'etl': 'ETL Developer Courses',
    'react': 'React Development Courses',
    'basic': 'General Programming Courses',
    // New course categories
    'rust': 'Rust Programming Courses',
    'modern-frontend': 'Modern Frontend Development Courses',
    'kubernetes-mastery': 'Kubernetes Mastery Courses',
    'edge-computing': 'Edge Computing Courses',
    'cross-platform': 'Cross-Platform Development Courses',
    'green-tech': 'Green Technology Courses',
    'no-code': 'No-Code Development Courses',
    'advanced-api': 'Advanced API Development Courses'
  };
  
  return titles[courseType] || `${courseType} Courses`;
};